/**
 * WORKSPACE-SWITCHER-001 — header pill + dropdown to move between the user's
 * workspaces. Renders nothing for single-workspace users. Pure navigation.
 *
 * Tone: `tone="dark"` for blue headers, `tone="light"` for white ones. When no
 * tone is passed the pill detects a `.header-blue` ancestor itself — the
 * QC-dashboard blank-label bug was a light pill inside a blue header whose
 * `.header-blue .text-slate-700 { color: white }` rule erased the label.
 * Explicit colour tokens (not slate utilities) keep the label readable in
 * both shells, so no per-dashboard workaround is needed.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Check, Landmark, ClipboardList, LayoutGrid, Search, ShieldCheck, Receipt } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { usePermissions } from '../hooks/usePermissions';
import { deriveWorkspaces, resolveActiveWorkspace, setActiveWorkspace, isWorkspaceNeutralPath } from '../utils/workspaces';

const ICONS = { order_desk: ClipboardList, jurisdiction_requestor: Landmark, title_search: Search, tax_processor: Receipt, qc_specialist: ShieldCheck };
const WorkspaceIcon = ({ id, className }) => {
  const Icon = ICONS[id] || LayoutGrid;
  return <Icon className={className} />;
};

export const WorkspaceSwitcher = ({ user, tone }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = usePermissions();
  const rootRef = useRef(null);
  const [autoDark, setAutoDark] = useState(false);
  const workspaces = deriveWorkspaces(user, permissions);

  useLayoutEffect(() => {
    if (tone || !rootRef.current) return;
    setAutoDark(Boolean(rootRef.current.closest('.header-blue')));
  }, [tone, workspaces.length]);

  if (workspaces.length < 2) return null;

  const active = resolveActiveWorkspace(workspaces, location.pathname);
  const dark = tone ? tone === 'dark' : autoDark;

  const choose = (ws) => {
    setActiveWorkspace(ws.id);
    // WS-CONTEXT-001: from a neutral page (My Workspace, Team, Analytics…) the displayed
    // workspace is only remembered, so re-choosing it must still return to its module home.
    if (ws.id !== active?.id || isWorkspaceNeutralPath(location.pathname)) navigate(ws.home);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          ref={rootRef}
          type="button"
          data-testid="workspace-switcher-trigger"
          title={`Workspace: ${active?.label || ''}`}
          className="flex h-8 shrink-0 items-center gap-1.5 rounded-md border pl-2 pr-1.5 text-xs font-semibold leading-none transition-[background-color,border-color] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          style={dark
            ? { color: '#ffffff', borderColor: 'rgba(255,255,255,0.28)', backgroundColor: 'rgba(255,255,255,0.12)' }
            : { color: '#1e293b', borderColor: '#e2e8f0', backgroundColor: '#ffffff' }}
        >
          <WorkspaceIcon id={active?.id} className="h-3.5 w-3.5 shrink-0" />
          <span
            className="max-w-[160px] truncate sm:max-w-[200px]"
            style={{ color: dark ? '#ffffff' : '#1e293b' }}
            data-testid="workspace-switcher-active"
          >
            {active?.label}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" style={{ opacity: 0.75 }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-1.5" data-testid="workspace-switcher-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Switch workspace</DropdownMenuLabel>
        {workspaces.map((ws) => {
          const isActive = ws.id === active?.id;
          return (
            <DropdownMenuItem
              key={ws.id}
              onClick={() => choose(ws)}
              data-testid={`workspace-option-${ws.id}`}
              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 cursor-pointer ${isActive ? 'bg-blue-50/80' : ''}`}
            >
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                <WorkspaceIcon id={ws.id} className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block truncate text-sm font-semibold ${isActive ? 'text-blue-700' : 'text-slate-800'}`}>{ws.label}</span>
                <span className="block truncate text-[11px] text-slate-500 leading-snug">{ws.description}</span>
              </span>
              {isActive && <Check className="h-4 w-4 shrink-0 text-blue-600" strokeWidth={2.5} />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <p className="px-2.5 py-1 text-[11px] text-slate-400 leading-snug">
          Same account, session and permissions — only the view changes.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default WorkspaceSwitcher;
